import React, { useMemo } from 'react';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { ChevronLeft, ChevronRight, MyLocation } from '@mui/icons-material';
import type { FlatExecuteStep } from '@/types/run-stage.types';
import { StepPill } from './StepPill';
import { GoToStepDropdown } from './GoToStepDropdown';

interface StepNavigatorProps {
  flatSteps: FlatExecuteStep[];
  currentIndex: number;
  onGoToStep: (index: number) => void;
  onPrev: () => void;
  onNext: () => void;
  onGoToActiveStep?: () => void;
  disabled?: boolean;
}

export const StepNavigator: React.FC<StepNavigatorProps> = ({
  flatSteps,
  currentIndex,
  onGoToStep,
  onPrev,
  onNext,
  onGoToActiveStep,
  disabled = false,
}) => {
  const canPrev = currentIndex > 0 && !disabled;
  const canNext = currentIndex < flatSteps.length - 1 && !disabled;
  const currentStep = flatSteps[currentIndex];

  // Determine if we're already on the active step
  const activeStepIndex = useMemo(() => {
    return flatSteps.findIndex(
      (fs) => !fs.step.completed && fs.step.status !== 'COMPLETED' && fs.step.status !== 'SKIPPED'
    );
  }, [flatSteps]);
  const isOnActiveStep = activeStepIndex === currentIndex || activeStepIndex === -1;

  return (
    <Box
      sx={{
        px: 2,
        py: 1,
        backgroundColor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* Top: prev/next + pill strip */}
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
        <IconButton size="small" onClick={onPrev} disabled={!canPrev} aria-label="Previous step">
          <ChevronLeft />
        </IconButton>

        {/* Pill strip — scrollable */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            overflowX: 'auto',
            py: 0.5,
            '&::-webkit-scrollbar': { height: 4 },
            '&::-webkit-scrollbar-thumb': { backgroundColor: 'action.disabled', borderRadius: 2 },
          }}
        >
          {flatSteps.map((fs, idx) => (
            <StepPill
              key={`${fs.step.stepId}-${fs.step.cloneNumber ?? 0}-${idx}`}
              flatStep={fs}
              isActive={idx === currentIndex}
              onClick={() => onGoToStep(idx)}
            />
          ))}
        </Box>

        <IconButton size="small" onClick={onNext} disabled={!canNext} aria-label="Next step">
          <ChevronRight />
        </IconButton>
      </Stack>

      {/* Bottom: current step info + go-to active + dropdown */}
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
        {currentStep && (
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {currentStep.displayName}
          </Typography>
        )}
        {currentStep?.step.user && (
          <Typography variant="caption" color="text.secondary">
            Assigned: {currentStep.step.user}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        {onGoToActiveStep && (
          <Button
            size="small"
            startIcon={<MyLocation />}
            onClick={onGoToActiveStep}
            disabled={isOnActiveStep || disabled}
            sx={{ textTransform: 'none' }}
          >
            Go to Active Step
          </Button>
        )}
        <GoToStepDropdown
          flatSteps={flatSteps}
          currentIndex={currentIndex}
          onSelect={onGoToStep}
        />
      </Stack>
    </Box>
  );
};
